import * as Select from '@radix-ui/react-select';
import styled from 'styled-components';
import { basicHide, basicShow } from '../styles';
import { StyledListItemType } from './Dropdown.types';

type DropdownContainerProps = {
  width?: string;
  isOpen: boolean;
};

export const DropdownContainer = styled.div<DropdownContainerProps>`
  position: relative;
  width: ${({ width }) => width || 'auto'};
  div.input-container {
    padding-right: 8px;
  }
  label.input-label {
    margin-bottom: 4px;
    margin-left: 2px;
  }
  span.input-helper {
    margin-left: 2px;
    margin-top: 2px;
    display: block;
  }
`;

export const ItemsContainer = styled.ul<{ isOpen: boolean }>`
  position: absolute;
  max-height: 232px;
  width: calc(100% - 16px);
  padding: 8px;
  margin: 8px 0 0 0;
  overflow: scroll;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  list-style: none;
  z-index: 1000;
  border-radius: 2px 2px 8px 8px;
  border: 1px solid var(--color-smoke-50);
  background: white;
  box-shadow: -15px 20px 40px 0px rgba(0, 0, 0, 0.04);
`;

export const ListItem = styled.li<StyledListItemType>`
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  color: inherit;
  background: inherit;
`;

export const TextAddButton = styled.div`
  cursor: pointer;
  color: var(--color-azure-800);
`;

export const LoaderContainer = styled.div`
  min-height: 128px;
  display: grid;
  place-content: center;
`;

type SelectTriggerProps = {
  disabled: boolean;
  isErrored: boolean;
  width?: string;
};

export const StyledSelect = styled.div`
  overflow: hidden;
  font-size: 14px;
`;
export const SelectRoot = styled(Select.Root)``;
export const SelectGroup = styled(Select.Group)`
  z-index: 100;
`;
export const SelectValue = styled(Select.Value)`
  flex: 1;
`;
export const SelectPortal = styled(Select.Portal)``;

export const SelectItem = styled(Select.Item)`
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  outline: none;
  span {
    width: 100%;
    word-wrap: break-word;
  }
  &[data-disabled] {
    pointer-events: none;
  }
  &[data-state='checked'] {
    background-color: var(--color-azure-50);
    color: var(--color-azure-800);
    font-weight: 600;
  }
  &[data-highlighted] {
    background-color: var(--color-smoke-50);
  }
  &:hover {
    background-color: var(--color-smoke-50);
  }
`;

export const SelectLabelDivider = styled.hr`
  flex: 1;
  height: 0px;
  border-style: solid;
  border-top: unset;
  border-right: unset;
  border-left: unset;
  border-bottom: 1px solid var(--color-smoke-50);
  margin: 0;
`;

export const SelectLabel = styled(Select.Label)`
  color: var(--color-elephant-900);
  padding: 8px 12px;
  user-select: none;
  pointer-events: none;
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const SelectContent = styled(Select.Content)`
  position: absolute;
  max-height: 232px;
  padding: 8px;
  overflow: scroll;
  list-style: none;
  font-size: 14px;

  z-index: 1000;
  border-radius: 2px 2px 8px 8px;
  background-color: white;
  border: 1px solid #b9b9b9;
  box-shadow: rgb(99 100 102 / 10%) 0px 0px 0px 1px,
    rgb(166 166 166 / 15%) 0px 1px 4px 0px,
    rgba(24, 50, 71, 0.05) 0px 6px 14px 0px;
  width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);
  &[data-state='open'] {
    animation-name: ${basicShow};
  }
  &[data-state='closed'] {
    animation-name: ${basicHide};
  }
  &[data-state='checked'] {
    background-color: var(--color-azure-50);
  }
  &[data-highlighted] {
    background-color: var(--color-smoke-50);
  }
`;

export const SelectItemText = styled(Select.ItemText)``;
export const SelectViewport = styled(Select.Viewport)``;
export const SelectItemIndicator = styled(Select.ItemIndicator)``;
export const SelectScrollUpButton = styled(Select.ScrollUpButton)`
  display: grid;
  place-content: center;
  margin-bottom: 8px;
`;

export const SelectIcon = styled(Select.Icon)`
  transform: rotate(-180deg);
  transition: transform 0.2s ease-in-out;
  display: grid;
  place-content: center;
`;

export const SelectTrigger = styled(Select.Trigger)<SelectTriggerProps>`
  width: 100%;
  padding: 6px 12px;
  display: flex;
  gap: 8px;
  background-color: var(--color-milk);
  border: 1px solid var(--color-smoke-100);
  align-items: center;
  justify-content: space-between;
  outline: none;
  border: 1px solid var(--color-smoke-50);
  border-radius: 4px;
  transition: all 0.2s ease-in-out;

  border: 1px solid
    ${({ disabled, isErrored }) =>
      disabled
        ? 'var(--color-smoke-50)'
        : isErrored
        ? 'var(--color-persimmon-800)'
        : 'var(--color-smoke-100)'};
  background-color: ${({ disabled }) =>
    disabled ? 'var(--color-smoke-50)' : 'var(--color-milk)'};

  span:first-child {
    flex: 1;
    text-align: left;
    width: 90%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &[data-state='open'] {
    .SelectIcon {
      transform: rotate(0deg);
    }
  }
`;
